import {
  FORMULA_EMPTY,
  FORMULA_GRAMMAR_INVALID,
  FORMULA_UNKNOWN_VARIABLE,
} from "./formulaErrorCodes";
import { type LexToken, tokenize } from "./tokenize";

export type FormulaValidationError =
  | { code: typeof FORMULA_EMPTY }
  | { code: typeof FORMULA_GRAMMAR_INVALID; offset?: number }
  | {
      code: typeof FORMULA_UNKNOWN_VARIABLE;
      unknownCode: string;
      availableCodes: string[];
    };

export type FormulaValidationResult =
  | { ok: true }
  | ({ ok: false } & FormulaValidationError);

interface SymbolSets {
  allowedCodes: string[];
  opDataCodes: string[];
}

// Variable codes are always uppercase (mirrors the backend gotcha).
const variableCodePattern = /[A-Z][A-Z0-9_]*/g;

function isStructurallyValid(tokens: LexToken[]): boolean {
  let depth = 0;
  let expectOperand = true;
  for (const token of tokens) {
    switch (token.kind) {
      case "variable":
        if (!expectOperand) return false;
        expectOperand = false;
        break;
      case "openParen":
        if (!expectOperand) return false;
        depth++;
        break;
      case "closeParen":
        if (expectOperand) return false;
        depth--;
        if (depth < 0) return false;
        break;
      case "operator":
        if (expectOperand) return false;
        expectOperand = true;
        break;
    }
  }
  return depth === 0 && !expectOperand;
}

export function validateFormula(
  expression: string,
  { allowedCodes, opDataCodes }: SymbolSets,
): FormulaValidationResult {
  if (expression.trim() === "") return { ok: false, code: FORMULA_EMPTY };

  const tokens = tokenize(expression);
  if (tokens === null || !isStructurallyValid(tokens)) {
    return { ok: false, code: FORMULA_GRAMMAR_INVALID };
  }

  const known = new Set([...allowedCodes, ...opDataCodes]);
  const codes = expression.match(variableCodePattern) ?? [];
  for (const code of codes) {
    if (!known.has(code)) {
      return {
        ok: false,
        code: FORMULA_UNKNOWN_VARIABLE,
        unknownCode: code,
        // Default (ordinal/UTF-16) sort String.sorted() to match Kotlin's on the backend —
        // localeCompare would order underscores differently (e.g. A_ vs AA) and break parity.
        availableCodes: [...allowedCodes].sort(),
      };
    }
  }

  return { ok: true };
}
